import { saveAs } from 'file-saver'
import $http from '@/libs/axios'
import LocalizationService from '@/services/localization-service'
import FormsService from '@/services/forms-service'
import { withSizeGuideRequestPayload } from '@/views/admin/setup/menu-setup/my-menu/utils/size-guide-helper'

class MyMenuAPIs {
  static async fetchMenu(branchId: number, provider: string) {
    return (await $http.get(`/my-menu/${branchId}${provider ? `?provider=${provider}` : ''}`)).data
  }

  static async fetchMenuForOrders(branchId: number, provider = '') {
    return (await $http.get(`/my-menu-for-orders/${branchId}${provider ? `?provider=${provider}` : ''}`)).data
  }

  static async useTemplate(branchId: number) {
    return (await $http.get(`/menu/use-template/${branchId}`)).data
  }

  static async deleteItem(itemId: number, current: boolean) {
    return (await $http.post('/items/delete', { _method: 'delete', item_id: itemId, current })).data
  }

  static async deleteCategory(category: number, current: boolean) {
    return (await $http.post('/categories/delete', { _method: 'delete', category_id: category, current })).data
  }

  static async move(type: 'categories' | 'items', category: number, ids: string) {
    return (await $http.put(`/${type}/move`, { category, ids })).data
  }

  static async duplicate(type: 'categories' | 'items', parent_id: number, name: any, ids: string) {
    return (await $http.put(`/${type}/duplicate`, { parent_id, ids, name })).data
  }

  static async pin(category_id: number, pin: boolean | number, pin_from?: string, pin_to?: string) {
    return (await $http.put('/categories/update', {
      category_id, pin, pin_from, pin_to,
    })).data
  }

  static async createCategory(payload: any) {
    return (await $http.post('/categories/create', payload)).data
  }

  static async createItem(payload: any) {
    return (await $http.post('/items/create', withSizeGuideRequestPayload(payload))).data
  }

  static getImportUrl(type: string, branchId: number) {
    if (type === 'items') return '/items/import'
    if (type === 'modifiers') return '/modifiers/import'
    return `/${type}/import/${branchId}`
  }

  static getTemplateUrl(type: string, branchId: number) {
    if (type === 'items') return '/items/import-template'
    if (type === 'modifiers') return '/modifiers/import-template'
    return `/${type}/export/${branchId}`
  }

  static getTemplateFileName(type: string) {
    if (type === 'items') return 'items-import-template.xlsx'
    if (type === 'modifiers') return 'modifiers-import-template.xlsx'
    return `${type}-template.xlsx`
  }

  static async import(branchId: number, body: any, type: string) {
    const headers = { 'Content-Type': 'multipart/form-data' }
    return (await $http.post(MyMenuAPIs.getImportUrl(type, branchId), body, {
      headers,
      keepErrorPayload: true,
    })).data
  }

  static async highlightCategory(branchId: number, body: any) {
    return (await $http.post(`categories/highlight/${branchId}`, body, { headers: FormsService.MULTIPART_HEADER })).data
  }

  static async getHighlightCategory(branchId: number) {
    return (await $http.get(`categories/highlight/${branchId}`)).data
  }

  static async getTemplate($branchId: number, type: string) {
    const file = (await $http.get(MyMenuAPIs.getTemplateUrl(type, $branchId), { responseType: 'blob' })).data
    return saveAs(file, MyMenuAPIs.getTemplateFileName(type))
  }

  static async getItem(id: number) {
    const { data } = await $http.get(`/items/view/${id}`)
    const { item } = data
    if (item.category) {
      item.category.name = LocalizationService.getTranslate(item.category, 'name') ?? ''
    }
    return item
  }

  static async getPriceUnits() {
    return (await $http.get('/price-units/list')).data
  }

  static async getCategory(id: number) {
    return (await $http.get(`/categories/view/${id}`)).data.category
  }

  static async editItem(payload: any) {
    return (await $http.put('/items/update', withSizeGuideRequestPayload(payload))).data
  }

  static async generateSku(itemId: any) {
    return (await $http.get(`/items/sku/${itemId}`)).data
  }

  static async editCategory(payload: any) {
    return (await $http.put('/categories/update', payload)).data
  }

  static async sortMenu(branchId: any, sortType: 'oldest' | 'newest' | 'az' | 'za') {
    return (await $http.get(`/menu-sort/${branchId}/${sortType}`)).data
  }

  static async sortItem(payload: any) {
    return (await $http.put('/items/sort', payload)).data
  }

  static async sortCategory(payload: any) {
    return (await $http.put('/categories/sort', payload)).data
  }

  static async ingredients() {
    const locale = LocalizationService.getCurrentLocale()
    return (await $http.get(`/ingredients?locale=${locale}`)).data.ingredients
  }

  static async dropTemplate(branchId: number) {
    return (await $http.put(`/menu/drop-template/${branchId}`)).data
  }

  static async getSmartCategories(branchId: number) {
    return (await $http.get(`/my-menu/smart-categories/${branchId}`)).data
  }

  static async updateSmartCategories(branchId: number, data: any) {
    return (await $http.post(`/my-menu/smart-categories/${branchId}`, { smart_categories: data })).data
  }

  static async restoreCategory(id: number) {
    return (await $http.put(`/categories/restore/${id}`)).data
  }

  static async restoreItem(id: number) {
    return (await $http.put(`/items/restore/${id}`)).data
  }

  static async getCrossSelling(branchId: number) {
    return (await $http.get(`/my-menu/cross-selling/${branchId}`)).data
  }

  static async crossSelling(branchId: number, payload: any) {
    return (await $http.post(`/my-menu/cross-selling/${branchId}`, payload)).data
  }
}

export default MyMenuAPIs
